import React from 'react';
import { useLocation } from 'react-router-dom';
import useBusinessTranslation from '../../../hooks/useBusinessTranslation';
import PageHeader from '../../components/misc/PageHeader';
import FinancialBalancePage from './FinancialBalancePage';
import FinancialMemberBalancePage from './FinancialMemberBalancePage';
import FinancialProfitLossPage from './FinancialProfitLossPage';

export default function ReportsPage(): JSX.Element {
  const { t } = useBusinessTranslation();
  const location = useLocation();
  
  // Set initial tab based on the current path or state
  const getInitialTab = (): 'profitLoss' | 'balance' | 'members' => {
    // Check if state indicates which tab to show
    if (location.state?.activeTab) {
      return location.state.activeTab;
    }
    
    if (location.pathname.includes('/financial/balance')) {
      return 'balance';
    }
    if (location.pathname.includes('/financial/member-balance')) {
      return 'members';
    }
    return 'members';
  };
  
  const [activeTab, setActiveTab] = React.useState<'profitLoss' | 'balance' | 'members'>(getInitialTab());

  // Respond to navigation that updates location.state without changing the path
  React.useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  return (
    <>
      <div>
        <PageHeader
          title={t('page.financial.reports.title')}
          subtitle={t('page.financial.reports.subtitle')}
        />

        {/* Tabs */}
        <div className="mb-6 flex gap-4 border-b border-primary-300">
          {(['members', 'profitLoss', 'balance'] as const).map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              onClick={() => setActiveTab(tabKey)}
              className={`relative px-4 pb-1 pt-2 font-medium transition-all duration-150 ${
                activeTab === tabKey
                  ? 'text-primary-700 -mb-[1px] rounded-t-md border border-primary-300 border-b-secondary-50'
                  : 'text-primary-600 hover:text-primary-800'
              }`}>
              {t(`page.financial.reports.tabs.${tabKey}`)}
            </button>
          ))}
        </div>

        <div className="w-full">
          {activeTab === 'profitLoss' && <FinancialProfitLossPage hideHeader />}
          {activeTab === 'balance' && <FinancialBalancePage hideHeader />}
          {activeTab === 'members' && <FinancialMemberBalancePage hideHeader />}
        </div>
      </div>
    </>
  );
}
